import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { APP_CONFIG } from '@config/app.config';
import { useWarehouses } from '@features/warehouses/hooks/useWarehouseOperations';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import { RuleOperator } from '@domain/inventory/enums/RuleOperator.enum';
import { RuleField } from '@domain/inventory/enums/RuleField.enum';
import { Connection } from '@domain/connection/models/Connection';
import { ClientType } from '@domain/connection/models/Connector';
import ShopifyConnectForm from '@features/connections/shopify/components/ShopifyConnectForm';
import WarehouseConnectionSelect from '../WarehouseConnectionSelect';
import { useShopifyConnector, useShopifySync } from '@features/connections/shopify/hooks/useShopifyConnector';
import { useSearchParams } from 'react-router-dom';

interface ShopifyWarehouseFormProps {
  onClose: () => void;
  onBack: () => void;
}

const ShopifyWarehouseForm: React.FC<ShopifyWarehouseFormProps> = ({
  onClose,
  onBack,
}) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { createWarehouse, isCreating } = useWarehouses();
  const { connector } = useShopifyConnector();
  const { syncInventory } = useShopifySync();

  const [form, setForm] = useState({
    name: 'Shopify',
    location: '',
  });
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [showCreateConnection, setShowCreateConnection] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const isLoading = isCreating || isSyncing;

  useEffect(() => {
    if (searchParams.get('shopify_connected') !== '1') return;
    setShowCreateConnection(false);
    queryClient.invalidateQueries({ queryKey: ['connections'] });
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('shopify_connected');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  const handleSubmit = async () => {
    if (!selectedConnection) return;

    await createWarehouse({
      name: form.name,
      location: form.location,
      warehouseConnections: [
        {
          sourceType: WarehouseConnectionType.SHOPIFY,
          mutationType: MutationType.SYNC,
          rules: [
            {
              operator: RuleOperator.EQUALS,
              field: RuleField.CONNECTION_ID,
              value: selectedConnection.id,
            },
          ],
        },
      ],
    });

    try {
      setIsSyncing(true);
      await syncInventory(selectedConnection.id);
    } finally {
      setIsSyncing(false);
    }

    onClose();
  };

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center">
            <IconButton onClick={onBack} color="inherit">
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" component="div">
              Sync with Shopify
            </Typography>
          </Stack>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ flex: 1 }}>
        <Stack gap={3} sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: 100, height: 60, borderRadius: 1, border: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 2 }}>
              <img src={connector?.image} alt="Shopify" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
            </Box>
            <Box>
              <Typography variant="h6" color="text.primary">
                Sync your inventory with Shopify
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Your Shopify inventory will be synced with your warehouse inventory.
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              Warehouse details
            </Typography>
            <Stack gap={2} direction="row">
              <TextField
                label="Warehouse name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Location"
                value={form.location}
                onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
                fullWidth
                size="small"
                placeholder="e.g., Amsterdam, Netherlands"
              />
            </Stack>
          </Stack>

          <WarehouseConnectionSelect
            connectorType={ClientType.SHOPIFY}
            selectedConnection={selectedConnection}
            onSelect={setSelectedConnection}
            onCreateConnection={() => setShowCreateConnection(true)}
            connectorName="Shopify"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedConnection || isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Create warehouse'}
        </Button>
      </DialogActions>

      <Dialog
        open={showCreateConnection}
        onClose={() => setShowCreateConnection(false)}
        maxWidth="sm"
      >
        <ShopifyConnectForm onClose={() => setShowCreateConnection(false)} />
      </Dialog>
    </>
  );
};

export default ShopifyWarehouseForm;
